import { Text } from '@shared/components';
import { ActionTimelineEvent } from '@shared/components/events-timeline/components/action-timeline-event';
import { useToast } from '@shared/components/toast';
import {
  TriggerPortalAppointmentSchedulingMutation,
  useTriggerPortalAppointmentSchedulingMutation as useSchedulePortal,
} from '@shared/generated/graphql';
import { useGetAvailabilityCheck } from '@shared/hooks/useSchedulePortalAvailability';
import { PortalTypeSpecificGetAvailabilityInputFields } from 'clerk_common/types';
import { useState } from 'react';
import { APIPortal } from '../Portals/APIPortal';
import { ApptStatusChip } from './ApptStatusChip';
import { ScheduleButton, StopProps } from './Stop';
import { knownAndInProgress, notYetScheduledOrUnknown } from './util';

export function APIPortalStop({
  stop,
  index,
  shipmentId,
  refetchShipment,
}: StopProps) {
  const [submitEnabled, setSubmitEnabled] = useState<boolean>(true);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [refetchLoading, setRefetchLoading] = useState<boolean>(false);
  const [inputFields, setInputFields] =
    useState<PortalTypeSpecificGetAvailabilityInputFields | null>(null);

  const checkParams = {
    shipmentId,
    index,
    movementStopId: stop.movementStopId,
  };
  const result = useGetAvailabilityCheck(checkParams);
  const checkFields = result.check?.fields;
  const { loading: availCheckLoading, refetch } = result;

  const options = { onCompleted: onPortalCompleted };
  const [triggerPortal, ctx] = useSchedulePortal(options);
  const { loading: scheduleLoading } = ctx;

  const { sendToast } = useToast();

  async function schedulePortalStop() {
    const stopsIndexToSchedule = index;
    const dt = selectedDate;
    const fields = inputFields ?? {};
    const input = {
      shipmentId,
      stopsIndexToSchedule,
      dt,
      fields,
      movementStopId: stop.movementStopId,
    };
    setSubmitEnabled(false);
    await triggerPortal({ variables: { input } });
  }

  async function onPortalCompleted(
    data: TriggerPortalAppointmentSchedulingMutation
  ) {
    const { success, error } = data.triggerPortalAppointmentScheduling;
    if (!success && error) {
      sendToast(error);
    }
    if (success) {
      await refetchShipment();
    }
    setSubmitEnabled(true);
  }

  async function handleCustomFieldsChange(
    inputFields: PortalTypeSpecificGetAvailabilityInputFields
  ) {
    const input = {
      shipmentId,
      stopsIndex: index,
      fields: inputFields,
      movementStopId: stop.movementStopId,
    };
    setRefetchLoading(true);
    await refetch({ input });
    setRefetchLoading(false);
  }

  const portalLoading = availCheckLoading || refetchLoading;
  const status = stop.appointmentStatus;
  const isNotYetScheduled = notYetScheduledOrUnknown(status);
  const isInProgress = knownAndInProgress(status);
  const noTimes = areNoTimes(result.check?.times);

  return (
    <ActionTimelineEvent type={stop.stopType}>
      <div className="mb-4 ml-2 mt-1 text-gray-700">
        <Text isHeavy type="body-xs">
          {stop.locationLabel}
        </Text>
        <Text type="body-xs">{stop.address.addressOne}</Text>
        {isNotYetScheduled && (
          <>
            <div className="my-3 rounded-md border bg-gray-50 p-2">
              {!portalLoading && (
                <APIPortal
                  times={result.check?.times ?? []}
                  selectedDate={selectedDate}
                  setSelectedDate={setSelectedDate}
                  info={result.check?.info}
                  fields={checkFields ?? undefined}
                  handleFieldsChange={handleCustomFieldsChange}
                  setInputFields={setInputFields}
                  noTimes={noTimes}
                  stop={stop}
                />
              )}
              {portalLoading && <p>Loading...</p>}
            </div>
            <div>
              <ScheduleButton
                onPress={schedulePortalStop}
                disabled={scheduleLoading || !submitEnabled || noTimes}
              />
            </div>
          </>
        )}
        {isInProgress && <ApptStatusChip stop={stop} />}
      </div>
    </ActionTimelineEvent>
  );
}

function areNoTimes(times?: Date[]): boolean {
  if (!times) return false;
  return times.length === 0;
}
